import { Auth, CognitoUser } from '@aws-amplify/auth';
import { awsConfig } from '../config/aws.config';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface RequestOptions<TRequestBody = unknown> {
  body?: TRequestBody;
  queryParams?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
}

class GekkoApi {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = (baseUrl || awsConfig.GEKKO_API_URL) as string;
  }

  /**
   * Get authentication token
   */
  private async getAuthToken(url?: string): Promise<string> {
    // Check if token is already stored in sessionStorage (e.g., for GekkoComponents)
    const storedToken = window.sessionStorage.getItem('gekko/authheader');
    if (storedToken) {
      return storedToken;
    }

    try {
      const session = await Auth.currentSession();
      const idTokenExpire = session.getIdToken().getExpiration();
      const refreshToken = session.getRefreshToken();
      const currentTimeSeconds = Math.round(+new Date() / 1000);

      if (idTokenExpire < currentTimeSeconds) {
        // Token expired, refresh it
        return new Promise((resolve, reject) => {
          Auth.currentAuthenticatedUser()
            .then((user: CognitoUser) => {
              user.refreshSession(refreshToken, (error, data) => {
                if (error) {
                  Auth.signOut();
                  window.location.reload();
                  reject(error);
                } else {
                  // Always use the access token
                  const token = `Bearer ${data.getAccessToken().getJwtToken()}`;
                  resolve(token);
                }
              });
            })
            .catch(reject);
        });
      } else {
        // Always use the access token
        return `Bearer ${session.getAccessToken().getJwtToken()}`;
      }
    } catch (error) {
      console.error('Failed to get auth token:', error);
      throw error;
    }
  }

  /**
   * Get Gekko information token
   */
  private async getGekkoInfoToken(): Promise<string | undefined> {
    try {
      const session = await Auth.currentSession();
      return session.getAccessToken().payload.gekkoInformationToken;
    } catch (error) {
      console.error('Failed to get Gekko information token:', error);
      return undefined;
    }
  }

  /**
   * Generic request method
   */
  private async request<TResponseData = unknown, TRequestBody = unknown>(
    method: HttpMethod,
    endpoint: string,
    options?: RequestOptions<TRequestBody>
  ): Promise<TResponseData> {
    try {
      const url = this.buildUrl(
        endpoint,
        method === 'GET' ? options?.queryParams : undefined
      );

      // Get auth token
      const token = await this.getAuthToken(endpoint);
      const gekkoInfoToken = await this.getGekkoInfoToken();

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        Accept: '*/*',
        'Access-Control-Allow-Origin': '*',
        Authorization: token,
        ...options?.headers,
      };

      // Add Gekko information token if available
      if (gekkoInfoToken) {
        headers.gekkoInformationToken = gekkoInfoToken;
      }

      const fetchOptions: RequestInit = {
        method,
        headers,
      };

      // Add body for non-GET requests
      if (method !== 'GET' && options?.body) {
        fetchOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(url, fetchOptions);

      // Handle 401 Unauthorized
      if (response.status === 401) {
        await Auth.signOut();
        window.location.reload();
        throw new GekkoApiError('Unauthorized - you have been signed out', 401);
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorBody;

        // Try to parse error response as JSON if possible
        try {
          errorBody = JSON.parse(errorText);
        } catch {
          errorBody = { message: errorText };
        }

        throw new GekkoApiError(
          `API error (${response.status}): ${errorText}`,
          response.status,
          errorBody
        );
      }

      // Network error handling similar to axios setup
      if (!response) {
        const errorMessage =
          process.env.NODE_ENV === 'production'
            ? 'There seems to be some problems.\n Please try again or refresh the page.\n Also try Logout and then Login again.\n If you still get this error contact us'
            : 'Problems to communicate with server.';

        throw new GekkoApiError(errorMessage, 0);
      }

      // Check if response is empty or not JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return (await response.json()) as TResponseData;
      }

      return {} as TResponseData;
    } catch (error) {
      // If the error is already a GekkoApiError, rethrow it
      if (error instanceof GekkoApiError) {
        throw error;
      }

      // Otherwise, create a new GekkoApiError
      console.error(
        `API request failed: ${
          error instanceof Error ? error.message : String(error)
        }`
      );

      throw new GekkoApiError(
        error instanceof Error ? error.message : 'Unknown API error',
        500
      );
    }
  }

  /**
   * Build URL with query parameters
   */
  private buildUrl(
    endpoint: string,
    queryParams?: Record<string, string | number | boolean | undefined>
  ): string {
    // Handle relative URLs
    const fullUrl = endpoint.startsWith('http')
      ? endpoint
      : `${this.baseUrl}${
          endpoint.startsWith('/') ? endpoint : `/${endpoint}`
        }`;

    const url = new URL(fullUrl);

    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  /**
   * GET request
   */
  public get<TResponseData = unknown>(
    endpoint: string,
    options?: RequestOptions
  ): Promise<TResponseData> {
    return this.request<TResponseData>('GET', endpoint, options);
  }

  /**
   * POST request
   */
  public post<TResponseData = unknown, TRequestBody = unknown>(
    endpoint: string,
    options?: RequestOptions<TRequestBody>
  ): Promise<TResponseData> {
    return this.request<TResponseData, TRequestBody>('POST', endpoint, options);
  }

  /**
   * PUT request
   */
  public put<TResponseData = unknown, TRequestBody = unknown>(
    endpoint: string,
    options?: RequestOptions<TRequestBody>
  ): Promise<TResponseData> {
    return this.request<TResponseData, TRequestBody>('PUT', endpoint, options);
  }

  /**
   * PATCH request
   */
  public patch<TResponseData = unknown, TRequestBody = unknown>(
    endpoint: string,
    options?: RequestOptions<TRequestBody>
  ): Promise<TResponseData> {
    return this.request<TResponseData, TRequestBody>(
      'PATCH',
      endpoint,
      options
    );
  }

  /**
   * DELETE request
   */
  public delete<TResponseData = unknown, TRequestBody = unknown>(
    endpoint: string,
    options?: RequestOptions<TRequestBody>
  ): Promise<TResponseData> {
    return this.request<TResponseData, TRequestBody>(
      'DELETE',
      endpoint,
      options
    );
  }
}

export class GekkoApiError extends Error {
  httpStatus: number;
  body: any;
  constructor(message: string, httpStatus: number, body?: any) {
    super(message);
    this.name = 'GekkoApiError';
    this.httpStatus = httpStatus;
    this.body = body;
  }
}

// Create and export a singleton instance
export const gekkoApi = new GekkoApi();

// Export the class for testing or custom instances
export default GekkoApi;
